import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request } from '../api/client';
import { DataTable, type Column } from '../components/DataTable';

interface CompraReembolsada {
  produto: string;
  valorCentavos: number;
  status: string;
  kiwifyOrderId: string;
  reembolsadaEm: string | null;
}

interface TicketItem {
  id: string;
  tipo: string;
  status: string;
  prioridade: string;
  resumo: string | null;
  cliente: { primeiroNome: string; telefone: string } | null;
  conversaId: string | null;
  createdAt: string;
  compraReembolsada: CompraReembolsada | null;
}

interface TicketPage {
  items: TicketItem[];
  total: number;
}

const PRIORIDADE_BADGE: Record<string, string> = {
  critica: 'text-sara-danger',
  alta: 'text-sara-warning',
  normal: 'text-sara-text-muted',
  baixa: 'text-sara-text/50',
};

const PRODUTO_LABEL: Record<string, string> = {
  front: 'Semente Energética',
  upsell1: 'Mapa Astral da Prosperidade',
  tiragem_avulsa: 'Tiragem de Cartas',
};

// Rótulos legíveis pros valores do enum TicketTipo (schema.prisma). A ordem
// aqui é a ordem exibida no seletor de filtro por tipo.
const TIPO_LABEL: Record<string, string> = {
  reembolso: 'Reembolso',
  risco_emocional: 'Risco emocional',
  cliente_agressiva: 'Cliente agressiva',
  fora_escopo: 'Fora de escopo',
  intervencao_solicitada: 'Intervenção solicitada',
  bonus_nao_recebido: 'Bônus não recebido',
  dados_mapa_invalidos: 'Dados de mapa inválidos',
  upsell_pendente: 'Upsell pendente',
  verificacao_pix_manual: 'Verificação Pix manual',
  mapa_astral_pendente: 'Mapa astral pendente',
  entrega_falhou: 'Entrega falhou',
  entrega_fantasma: 'Entrega fantasma',
  consulta_30min_agendamento: 'Consulta 30min (agendamento)',
  queixa_repeticao: 'Queixa de repetição',
  tiragem_loop_travado: 'Tiragem travada em loop',
  compra_nao_localizada: 'Compra não localizada',
  acesso_nao_informado: 'Acesso não informado',
  cobranca_contestada: 'Cobrança contestada',
  identidade_nao_confirmada: 'Identidade não confirmada',
  outro: 'Outro',
};

const TIPO_OPTIONS = Object.keys(TIPO_LABEL);

function formatBRL(cents: number): string {
  return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function TicketsPage(): JSX.Element {
  const initialFilters = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      status: params.get('status') ?? 'aberto',
      tipo: params.get('tipo') ?? '',
    };
  }, []);
  const [statusFilter, setStatusFilter] = useState(initialFilters.status);
  const [tipoFilter, setTipoFilter] = useState(initialFilters.tipo);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const queryClient = useQueryClient();

  // A tela mostrava só os 25 primeiros e não dizia que havia mais (auditoria
  // 360°, achado D10-02): com 39 abertos no Brasil, 14 ficavam invisíveis e
  // nada na tela sugeria que existiam. O backend já paginava — o painel é que
  // nunca pedia página nem lia o total.
  const [page, setPage] = useState(1);
  const pageSize = 25;

  // Limpa a seleção e volta pra primeira página quando muda o filtro.
  useEffect(() => {
    setSelectedIds(new Set());
    setPage(1);
  }, [statusFilter, tipoFilter]);

  const { data, isLoading, isError, refetch } = useQuery<TicketPage>({
    queryKey: ['tickets', statusFilter, tipoFilter, page],
    queryFn: () => {
      const params = new URLSearchParams({ status: statusFilter });
      if (tipoFilter) params.set('tipo', tipoFilter);
      params.set('page', String(page));
      params.set('pageSize', String(pageSize));
      return request<TicketPage>(`/admin/tickets?${params.toString()}`);
    },
  });

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // Filtro por tipo é server-side (o backend aplica o WHERE por tipo), então
  // a paginação não deixa nenhum ticket de fora — diferente do filtro no front.
  const filteredItems = data?.items;

  // "Assumir" põe seu nome no chamado e marca em atendimento. Os campos
  // existiam no banco desde sempre e ninguém escrevia neles — a fila não tinha
  // dono (achado D11-04). Pedido da dona pensando em ter mais gente depois.
  const assumir = useMutation({
    mutationFn: (id: string) => request(`/admin/tickets/${id}/assumir`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tickets'] }),
  });

  const resolver = useMutation({
    mutationFn: (id: string) =>
      request(`/admin/tickets/${id}/resolver`, { method: 'POST', body: {} }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tickets'] }),
  });

  const resolverBulk = useMutation({
    mutationFn: (ids: string[]) =>
      request<{ ok: true; resolved: number }>('/admin/tickets/resolver-bulk', {
        method: 'POST',
        body: { ids },
      }),
    onSuccess: () => {
      setSelectedIds(new Set());
      void queryClient.invalidateQueries({ queryKey: ['tickets'] });
    },
  });

  const visibleIds = useMemo(() => (filteredItems ?? []).map((t) => t.id), [filteredItems]);
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const someSelected = selectedIds.size > 0;

  const toggleOne = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAll = () =>
    setSelectedIds((prev) => {
      if (visibleIds.every((id) => prev.has(id))) return new Set();
      return new Set(visibleIds);
    });

  const abrirChatwoot = async (conversaId: string) => {
    try {
      const { url } = await request<{ url: string | null }>(
        `/admin/conversas/${conversaId}/chatwoot-link`,
      );
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        alert('Chatwoot não configurado ou contato não encontrado.');
      }
    } catch (err) {
      console.error(err);
      alert('Erro ao abrir conversa no Chatwoot. Tenta de novo.');
    }
  };

  const columns: Column<TicketItem>[] = [
    {
      key: 'select',
      header: (
        <input
          type="checkbox"
          aria-label="Selecionar todos os tickets visíveis"
          checked={allSelected}
          onChange={toggleAll}
          disabled={visibleIds.length === 0}
        />
      ),
      render: (r) => (
        <input
          type="checkbox"
          aria-label={`Selecionar ticket ${r.cliente?.primeiroNome ?? r.id}`}
          checked={selectedIds.has(r.id)}
          onChange={() => toggleOne(r.id)}
        />
      ),
    },
    {
      key: 'prioridade',
      header: 'Prioridade',
      render: (r) => <span className={PRIORIDADE_BADGE[r.prioridade]}>{r.prioridade}</span>,
    },
    { key: 'tipo', header: 'Tipo', render: (r) => TIPO_LABEL[r.tipo] ?? r.tipo },
    {
      key: 'cliente',
      header: 'Cliente',
      render: (r) => (r.cliente ? `${r.cliente.primeiroNome} (${r.cliente.telefone})` : '—'),
    },
    {
      key: 'resumo',
      header: 'Resumo',
      render: (r) => {
        if (r.tipo === 'reembolso' && r.compraReembolsada) {
          const c = r.compraReembolsada;
          const produto = PRODUTO_LABEL[c.produto] ?? c.produto;
          const statusLabel = c.status === 'estornada' ? 'chargeback' : 'reembolso';
          return (
            <div className="space-y-0.5">
              <div className="font-medium">
                {produto} — {formatBRL(c.valorCentavos)}
              </div>
              <div className="text-xs text-sara-text-muted">
                {statusLabel} · order {c.kiwifyOrderId}
              </div>
            </div>
          );
        }
        return r.resumo ?? '—';
      },
    },
    {
      key: 'createdAt',
      header: 'Aberto em',
      render: (r) => new Date(r.createdAt).toLocaleString('pt-BR'),
    },
    {
      key: 'actions',
      header: 'Ações',
      render: (r) => (
        <div className="flex flex-wrap gap-1">
          {r.conversaId && (
            <button
              type="button"
              className="btn-ghost text-xs"
              onClick={() => void abrirChatwoot(r.conversaId as string)}
            >
              Abrir no Chatwoot
            </button>
          )}
          {r.status === 'aberto' && (
            <button
              type="button"
              className="btn-ghost text-xs"
              onClick={() => assumir.mutate(r.id)}
              disabled={assumir.isPending}
              title="Marca como em atendimento e põe seu nome nele"
            >
              Assumir
            </button>
          )}
          <button
            type="button"
            className="btn-ghost text-xs"
            onClick={() => resolver.mutate(r.id)}
            disabled={resolver.isPending}
          >
            Resolver
          </button>
        </div>
      ),
    },
  ];

  return (
    <section aria-labelledby="tickets-heading" className="space-y-4">
      <h1 id="tickets-heading" className="text-2xl font-semibold">
        Tickets
      </h1>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <fieldset className="flex gap-2" role="radiogroup" aria-label="Filtro de status">
          {['aberto', 'em_atendimento', 'resolvido'].map((s) => (
            <button
              key={s}
              type="button"
              role="radio"
              aria-checked={statusFilter === s}
              onClick={() => setStatusFilter(s)}
              className={`btn-ghost text-xs ${statusFilter === s ? 'bg-sara-accent/20 border-sara-accent' : ''}`}
            >
              {s.replace('_', ' ')}
            </button>
          ))}
        </fieldset>
        <div className="flex items-center gap-2">
          <label htmlFor="tipo-filter" className="text-xs text-sara-text-muted">
            Tipo
          </label>
          <select
            id="tipo-filter"
            className="input max-w-xs text-xs"
            value={tipoFilter}
            onChange={(e) => setTipoFilter(e.target.value)}
          >
            <option value="">Todos os tipos</option>
            {TIPO_OPTIONS.map((t) => (
              <option key={t} value={t}>
                {TIPO_LABEL[t]}
              </option>
            ))}
          </select>
          {tipoFilter && (
            <button type="button" className="btn-ghost text-xs" onClick={() => setTipoFilter('')}>
              Limpar
            </button>
          )}
        </div>
      </div>

      {someSelected && (
        <div
          className="flex flex-wrap items-center gap-3 rounded border border-sara-accent/40 bg-sara-accent/10 px-4 py-2 text-sm"
          role="region"
          aria-label="Ações em lote"
        >
          <span className="font-medium">
            {selectedIds.size} ticket{selectedIds.size > 1 ? 's' : ''} selecionado
            {selectedIds.size > 1 ? 's' : ''}
          </span>
          <button
            type="button"
            className="btn-primary text-xs"
            disabled={resolverBulk.isPending}
            onClick={() => {
              if (
                window.confirm(
                  `Resolver ${selectedIds.size} ticket(s) selecionado(s)? Essa ação marca todos como resolvidos.`,
                )
              ) {
                resolverBulk.mutate([...selectedIds]);
              }
            }}
          >
            {resolverBulk.isPending ? 'Resolvendo…' : 'Resolver selecionados'}
          </button>
          <button
            type="button"
            className="btn-ghost text-xs"
            onClick={() => setSelectedIds(new Set())}
          >
            Limpar seleção
          </button>
        </div>
      )}

      <DataTable
        columns={columns}
        rows={filteredItems}
        isLoading={isLoading}
        isError={isError}
        onRetry={() => void refetch()}
        rowKey={(r) => r.id}
        emptyMessage={
          tipoFilter
            ? `Nenhum ticket do tipo "${TIPO_LABEL[tipoFilter] ?? tipoFilter}" nesse status.`
            : 'Nenhum ticket nesse status.'
        }
      />

      {total > 0 && (
        <div className="flex items-center gap-3 text-sm">
          <span className="text-sara-text-muted">
            {total} {total === 1 ? 'ticket' : 'tickets'} neste filtro
          </span>
          {totalPages > 1 && (
            <>
              <button
                type="button"
                className="px-2 py-1 rounded bg-sara-surface disabled:opacity-40"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                ← anterior
              </button>
              <span className="text-sara-text-muted">
                página {page} de {totalPages}
              </span>
              <button
                type="button"
                className="px-2 py-1 rounded bg-sara-surface disabled:opacity-40"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                próxima →
              </button>
            </>
          )}
        </div>
      )}
    </section>
  );
}
